import { useEffect, useRef, useState } from 'react';
import { Box, Flex, Icon, Input, InputGroup, Spinner, Stack, Text, Badge } from '@chakra-ui/react';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import api from '@/utils/api';
import { Content } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';
import { CourseItemDraft, uid } from '@/pages/courses/api';

interface CourseItemSearchProps {
  onAdd: (item: CourseItemDraft) => void;
  onDone?: () => void;
}

// Fully-snapshotted item draft from a library search hit — copies what we
// need at add time so later renames of the source content don't retroactively
// change the course (same rationale as planner's itemFromContent).
// workspace_name is only set on hits coming from a partner library, so it
// doubles as the source label; the caller's own unpublished items are
// labeled "Personal" since they aren't in the workspace library.
function itemFromContent(content: Content): CourseItemDraft {
  return {
    uid: uid(),
    content_id: content.id,
    content_reference_id: content.reference_id,
    content_type_id: content.content_type_id,
    content_type_name: content.content_type_name ?? '',
    content_name: content.name,
    source_label:
      content.status !== 'published' ? 'Personal' : content.workspace_name || 'My workspace',
  };
}

// Inline library search for the course builder. Unlike planner's ItemSearch
// there is no free-text custom item — course items always reference the
// content library (per the MVP requirements).
export default function CourseItemSearch({ onAdd, onDone }: CourseItemSearchProps) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!workspaceId) return;
    const q = debouncedQuery;
    if (!q) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let cancelled = false;
    api
      .get(
        // include_personal widens the published-only scope with the caller's own
        // unpublished items, so personal-library content can be added to courses too.
        `contents/search/${workspaceId}?q=${encodeURIComponent(q)}&status=published&limit=8&include_partners=1&include_personal=1`,
      )
      .then((res) => {
        if (cancelled) return;
        setResults(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, workspaceId]);

  const handleAdd = (content: Content) => {
    onAdd(itemFromContent(content));
    setQuery('');
    setResults([]);
    onDone?.();
  };

  return (
    <Box bg='bg.subtle' borderRadius='md' p={3}>
      <InputGroup startElement={<Icon as={MagnifyingGlassIcon} w={4} h={4} color='fg.subtle' />}>
        <Input
          ref={inputRef}
          size='sm'
          bg='bg.panel'
          placeholder='Search your libraries …'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </InputGroup>

      {isLoading && (
        <Flex justify='center' py={3}>
          <Spinner size='sm' />
        </Flex>
      )}

      {!isLoading && results.length > 0 && (
        <Stack gap={1} mt={2}>
          {results.map((content) => {
            const slot = slotFor(content.content_type_name ?? '');
            return (
              <Flex
                key={content.id}
                align='center'
                gap={2.5}
                px={2.5}
                py={2}
                borderRadius='sm'
                cursor='pointer'
                _hover={{ bg: 'bg.panel' }}
                onClick={() => handleAdd(content)}
              >
                <Icon as={PlusIcon} w={3.5} h={3.5} color='brand.fg' flexShrink={0} />
                <Text fontSize='sm' color='fg.strong' flex='1' truncate>
                  {content.name}
                </Text>
                {content.workspace_name && (
                  <Text fontSize='2xs' color='fg.muted' flexShrink={0}>
                    {content.workspace_name}
                  </Text>
                )}
                {content.status !== 'published' && (
                  <Badge bg='bg.muted' color='fg.muted' fontSize='2xs'>
                    Personal
                  </Badge>
                )}
                {content.content_type_name && (
                  <Badge bg={`slot${slot}.bg`} color={`slot${slot}.fg`} fontSize='2xs'>
                    {content.content_type_name}
                  </Badge>
                )}
              </Flex>
            );
          })}
        </Stack>
      )}

      {!isLoading && debouncedQuery && results.length === 0 && (
        <Text fontSize='xs' color='fg.muted' textAlign='center' py={3}>
          Nothing in your libraries matches &ldquo;{debouncedQuery}&rdquo;.
        </Text>
      )}
    </Box>
  );
}
